/*
 * sift-search.js — Sift storefront search, progressive enhancement for Empire.
 *
 * Vanilla ES module, no dependencies. Native Shopify search is the ALWAYS
 * available fallback:
 *   - the header form is never preventDefault-ed as a whole (Enter → /search);
 *   - on ANY suggest failure the flydown tears itself out and Empire's native
 *     predictive search resumes (it binds via jQuery delegation, so removing
 *     Sift's capture on the input is a clean handoff);
 *   - on ANY results-page failure the native server-rendered grid is left
 *     exactly as delivered — no error is ever shown to the customer.
 *
 * Security: DOM is built with createElement/textContent only — remote strings
 * never touch innerHTML. Meili `_formatted` highlight tags are split, not
 * injected. <img> src is set only when the URL host is exactly cdn.shopify.com.
 *
 * The pure-logic helpers at the top are exported for unit tests; the runtime
 * classes self-init only inside a browser that actually has the Empire markup.
 */

/* ============================ pure logic ============================ */

/** Trailing debounce. */
export function debounce(fn, ms) {
  let t = null;
  const wrapped = (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => {
      t = null;
      fn(...args);
    }, ms);
  };
  wrapped.cancel = () => {
    if (t) clearTimeout(t);
    t = null;
  };
  return wrapped;
}

/** True only for URLs whose host is exactly cdn.shopify.com. */
export function isCdnImage(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    return new URL(url).host === 'cdn.shopify.com';
  } catch {
    return false;
  }
}

/**
 * Split a Meili `_formatted` string on highlight markers into safe segments.
 * Returns [{ text, mark }] — the caller builds text nodes and <mark> nodes,
 * so any other markup-looking characters in the value stay literal text.
 * @returns {{text:string, mark:boolean}[]}
 */
export function parseHighlight(formatted, pre = '<em>', post = '</em>') {
  const out = [];
  if (formatted == null) return out;
  let s = String(formatted);
  while (s.length) {
    const a = s.indexOf(pre);
    if (a === -1) {
      out.push({ text: s, mark: false });
      break;
    }
    if (a > 0) out.push({ text: s.slice(0, a), mark: false });
    const rest = s.slice(a + pre.length);
    const b = rest.indexOf(post);
    if (b === -1) {
      // Unbalanced marker — treat the remainder as plain text, never as markup.
      out.push({ text: rest, mark: false });
      break;
    }
    out.push({ text: rest.slice(0, b), mark: true });
    s = rest.slice(b + post.length);
  }
  return out.filter((seg) => seg.text.length);
}

/**
 * Multi-value facets ride the wire as REPEATED params (`vendor=A&vendor=B`), NOT
 * comma-joined — a real catalog value may itself contain a comma (vendor
 * "Cape Cod Polish, Inc.", category "Food, Beverages & Tobacco"), so a comma
 * cannot double as the delimiter without tearing the value. The API accepts each
 * occurrence as one literal value.
 */
export const MULTI_FACETS = ['vendor', 'product_type', 'category'];
export const BOOL_FACETS = ['available', 'on_sale'];

/** Parse a results-page query string into a normalized state object. */
export function readParams(searchStr) {
  const p = new URLSearchParams(searchStr || '');
  const state = { q: (p.get('q') || '').trim(), sort: p.get('sort') || 'relevance', page: 1 };
  const page = parseInt(p.get('page') || '1', 10);
  state.page = Number.isFinite(page) && page > 1 ? page : 1;
  for (const f of MULTI_FACETS) {
    // Repeated params (`vendor=A&vendor=B`), NOT comma-joined: a facet VALUE may
    // itself contain a comma. getAll() keeps every value intact so a value like
    // "Cape Cod Polish, Inc." survives share/reload/back-button (AC-18).
    state[f] = p.getAll(f).map((s) => s.trim()).filter(Boolean);
  }
  for (const f of BOOL_FACETS) {
    const v = p.get(f);
    state[f] = v === 'true' ? true : v === 'false' ? false : null;
  }
  const pmin = p.get('price_min');
  const pmax = p.get('price_max');
  state.price_min = pmin != null && pmin !== '' && Number.isFinite(+pmin) ? +pmin : null;
  state.price_max = pmax != null && pmax !== '' && Number.isFinite(+pmax) ? +pmax : null;
  return state;
}

/** Serialize a state object back to a query string (shared by URL + API call). */
export function buildQuery(state) {
  const p = new URLSearchParams();
  if (state.q) p.set('q', state.q);
  for (const f of MULTI_FACETS) {
    const arr = state[f];
    // One repeated param per value; URLSearchParams percent-encodes any internal
    // comma so the API receives each value whole (never split on comma).
    if (Array.isArray(arr)) for (const v of arr) if (v) p.append(f, v);
  }
  for (const f of BOOL_FACETS) {
    if (state[f] === true) p.set(f, 'true');
    else if (state[f] === false) p.set(f, 'false');
  }
  if (state.price_min != null) p.set('price_min', String(state.price_min));
  if (state.price_max != null) p.set('price_max', String(state.price_max));
  if (state.sort && state.sort !== 'relevance') p.set('sort', state.sort);
  if (state.page && state.page > 1) p.set('page', String(state.page));
  return p.toString();
}

/** Minimal money format — dollars → "$12.95" (currency symbol configurable). */
export function formatMoney(amount, symbol = '$') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return `${symbol}${n.toFixed(2)}`;
}

/* ============================ runtime ============================ */

const BACKOFF_KEY = 'sift:disabled_until';
const BACKOFF_MS = 5 * 60 * 1000;
const SUGGEST_TIMEOUT = 600;
const SEARCH_TIMEOUT = 2000;
// Empire's native LiveSearch binds keyup.live-search + focus.live-search DIRECTLY
// on [data-live-search-input], and change.live-search on [data-live-search-filter].
// Sift shields exactly these at document capture-phase while it is live.
const SHIELD_EVENTS = ['keyup', 'focus', 'change'];

function backoffActive() {
  try {
    const until = parseInt(localStorage.getItem(BACKOFF_KEY) || '0', 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}
function setBackoff() {
  try {
    localStorage.setItem(BACKOFF_KEY, String(Date.now() + BACKOFF_MS));
  } catch {
    /* private mode / disabled storage — flapping is bounded by the failed flag */
  }
}

/** el(tag, {class, text, attrs}, children) — createElement helper, no innerHTML. */
function el(tag, opts = {}, children = []) {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text != null) node.textContent = opts.text;
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
  for (const c of children) if (c) node.appendChild(c);
  return node;
}

/** Build highlighted title nodes (text + <mark>) into a parent element. */
function appendHighlighted(parent, formatted, fallback) {
  const segs = parseHighlight(formatted);
  if (!segs.length) {
    parent.appendChild(document.createTextNode(fallback || ''));
    return;
  }
  for (const seg of segs) {
    if (seg.mark) parent.appendChild(el('mark', { text: seg.text }));
    else parent.appendChild(document.createTextNode(seg.text));
  }
}

function abs(url, origin) {
  if (!url) return '#';
  if (/^https?:\/\//.test(url)) return url;
  return (origin || '') + url;
}

/* ---------------------------- flydown ---------------------------- */

/* Click beacon (Grady 2026-07-16): record which result the customer chose for
   a query — ground truth for ranking work. Fire-and-forget: analytics must
   never delay or break a navigation. text/plain keeps sendBeacon a CORS
   "simple request" (no preflight; the response is never read). */
function sendClickBeacon(cfg, a) {
  try {
    const pid = parseInt(a.dataset.siftPid, 10);
    if (!pid) return;
    const body = JSON.stringify({
      q: a.dataset.siftQ || '',
      id: pid,
      rank: parseInt(a.dataset.siftRank, 10) || 0,
      surface: a.dataset.siftSurface === 'suggest' ? 'suggest' : 'search',
    });
    const url = `${cfg.apiBase}/api/click`;
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return;
    fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'text/plain' }, keepalive: true }).catch(() => {});
  } catch (e) {
    /* never break a navigation for analytics */
  }
}

function installClickBeacon(cfg) {
  if (window.__siftBeacon) return;
  window.__siftBeacon = true;
  // Capture phase: fires even if a later handler stops propagation.
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target;
      const a = t && t.closest ? t.closest('a[data-sift-pid]') : null;
      if (a) sendClickBeacon(cfg, a);
    },
    true,
  );
}

class SiftFlydown {
  constructor(root, cfg) {
    this.cfg = cfg;
    this.root = root;
    this.input = root.querySelector('[data-live-search-input]');
    this.flydown = root.querySelector('[data-live-search-flydown]');
    this.results = root.querySelector('[data-live-search-results]');
    this.placeholder = root.querySelector('[data-live-search-placeholder]');
    this.ctrl = null;
    this.active = -1;
    this.options = [];
    this.failed = false;
    this.debounced = debounce(() => this.fetch(), 150);
    // Bound handlers kept so failover() can remove exactly what it added.
    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocDown = this.onDocDown.bind(this);
    // Document capture-phase shield that suppresses Empire's native LiveSearch.
    this.onShield = this.onShield.bind(this);
  }

  usable() {
    return this.input && this.flydown && this.results;
  }

  enhance() {
    const r = this.results;
    if (!r.id) r.id = 'sift-flydown-results';
    r.setAttribute('role', 'listbox');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', r.id);
    this.input.setAttribute('aria-haspopup', 'listbox');
    // Screen-reader count announcer.
    this.live = el('span', { class: 'sift-visually-hidden', attrs: { 'aria-live': 'polite', 'aria-atomic': 'true' } });
    this.flydown.appendChild(this.live);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    document.addEventListener('mousedown', this.onDocDown);
    // Suppress Empire's native predictive search. Empire binds keyup.live-search
    // + focus.live-search DIRECTLY on the input (and change.live-search on the
    // filter), and instantiates that LiveSearch ASYNCHRONOUSLY after a Shopify
    // script loads — so it may not even be bound when Sift boots, and it lives on
    // the input node itself where a same-element stopPropagation on the `input`
    // event has no effect on a `keyup`/`focus` handler. Shield instead at the
    // DOCUMENT in the CAPTURE phase: an ancestor capture listener always fires
    // before the input's own target-phase listeners regardless of bind order, so
    // stopPropagation there stops the event before Empire ever sees it. The shield
    // is removed on failover, letting native resume immediately (Empire's own
    // bindings were never touched). Verified against empire.js LiveSearch:29315.
    for (const type of SHIELD_EVENTS) document.addEventListener(type, this.onShield, true);
  }

  /**
   * Capture-phase suppressor for Empire's native predictive handlers. Acts only
   * on events targeting our own search input (keyup/focus) or the native category
   * filter (change), and only while Sift is live. Drives Sift's own focus-reopen
   * directly since stopPropagation prevents the input's own focus listener firing.
   */
  onShield(e) {
    if (this.failed) return;
    const t = e.target;
    if (!t || !this.root.contains(t)) return;
    if (e.type === 'change') {
      if (t.matches && t.matches('[data-live-search-filter]')) e.stopPropagation();
      return;
    }
    if (t !== this.input) return;
    e.stopPropagation(); // Empire's native keyup/focus handler never fires
    if (e.type === 'focus') this.onFocus();
  }

  onInput() {
    if (this.failed) return;
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.debounced.cancel();
      this.close();
      return;
    }
    this.debounced();
  }

  onFocus() {
    if (this.failed) return;
    if (this.input.value.trim().length >= 2 && this.options.length) this.open();
  }

  onDocDown(e) {
    if (!this.root.contains(e.target)) this.close();
  }

  async fetch() {
    const q = this.input.value.trim();
    if (q.length < 2) return this.close();
    if (this.ctrl) this.ctrl.abort();
    const ctrl = (this.ctrl = new AbortController());
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      ctrl.abort();
    }, SUGGEST_TIMEOUT);
    try {
      const url = `${this.cfg.apiBase}/api/suggest?q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
      clearTimeout(timer);
      if (!res.ok) throw new Error('http');
      const data = await res.json();
      if (ctrl.signal.aborted) return; // superseded by a newer keystroke
      this.render(data, q);
    } catch (err) {
      clearTimeout(timer);
      // An abort from a newer keystroke is normal; only a timeout or a real
      // network/HTTP/parse error is a failure that triggers native handoff.
      if (ctrl.signal.aborted && !timedOut) return;
      this.failover();
    }
  }

  render(data, q) {
    // Defense-in-depth against a stale reopen (Indent finding): only paint/open
    // if the input still holds exactly this query (>=2 chars) and Sift is live.
    // close() already aborts the in-flight fetch, so this rarely fires — but a
    // debounce that elapsed just before dismissal, or any future refactor, must
    // never reopen the listbox against a cleared/changed input.
    if (this.failed) return;
    if (q.length < 2 || this.input.value.trim() !== q) return;
    const products = Array.isArray(data.products) ? data.products : [];
    this.results.textContent = '';
    this.options = [];
    if (!products.length) {
      const row = el('a', {
        class: 'search-flydown--product sift-flydown--empty',
        attrs: { href: `${this.cfg.searchUrl}?q=${encodeURIComponent(q)}`, role: 'option', id: 'sift-o-all' },
      });
      row.appendChild(el('span', { class: 'search-flydown--product-text' }, [
        el('span', { class: 'search-flydown--product-title', text: this.cfg.strings.viewAll || `Search for "${q}"` }),
      ]));
      this.results.appendChild(row);
      this.options = [row];
    } else {
      products.forEach((p, i) => this.results.appendChild(this.card(p, i)));
    }
    if (this.placeholder) this.placeholder.hidden = true;
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.open();
    const n = typeof data.nbHits === 'number' ? data.nbHits : products.length;
    let liveMsg = this.cfg.strings.count ? this.cfg.strings.count.replace('{{count}}', n) : `${n} results`;
    // Zero-hit spell correction happened server-side — say so (visually + politely).
    if (data.corrected && products.length) {
      const hint = el('div', {
        class: 'sift-flydown--corrected',
        text: `Showing results for “${data.corrected}”`,
        attrs: { role: 'presentation' }, // non-option inside the listbox; announced via the live region
      });
      this.results.insertBefore(hint, this.results.firstChild);
      liveMsg += ` for “${data.corrected}”`;
    }
    this.live.textContent = liveMsg;
  }

  card(p, i) {
    const id = `sift-o-${i}`;
    const a = el('a', {
      class: 'search-flydown--product',
      attrs: { href: abs(p.url, this.cfg.origin), role: 'option', id, tabindex: '-1' },
    });
    a.dataset.siftPid = String(p.id || '');
    a.dataset.siftRank = String(i);
    a.dataset.siftSurface = 'suggest';
    a.dataset.siftQ = this.input.value.trim();
    // image (cdn-only)
    if (this.cfg.enableImages && p.image && isCdnImage(p.image.url)) {
      const wrap = el('span', { class: 'search-flydown--product-image' });
      const img = el('img', {
        attrs: { src: p.image.url, alt: p.image.alt || '', loading: 'lazy', width: '90', height: '90' },
      });
      wrap.appendChild(img);
      a.appendChild(wrap);
    }
    const text = el('span', { class: 'search-flydown--product-text' });
    const title = el('span', { class: 'search-flydown--product-title' });
    appendHighlighted(title, p._formatted && p._formatted.title, p.title);
    text.appendChild(title);
    if (p.price_min != null) {
      text.appendChild(
        el('span', {
          class: 'search-flydown--product-price',
          text: formatMoney(p.price_min, this.cfg.currencySymbol),
        }),
      );
    }
    a.appendChild(text);
    this.options.push(a);
    return a;
  }

  open() {
    this.root.classList.add('live-search--active');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    // Every dismiss path (input < 2 chars, click-away, Escape, failover) routes
    // through here, so this is where in-flight suggest work must stop. Without
    // this, a /api/suggest already on the wire resolves after close() and render()
    // reopens the flydown against a query the input no longer holds (Indent
    // stale-reopen finding). Cancel the pending debounce AND abort the in-flight
    // fetch — its post-await `if (ctrl.signal.aborted) return` then skips render.
    this.debounced.cancel();
    if (this.ctrl) {
      this.ctrl.abort();
      this.ctrl = null;
    }
    this.root.classList.remove('live-search--active');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
  }

  move(delta) {
    if (!this.options.length) return;
    this.active = (this.active + delta + this.options.length) % this.options.length;
    this.options.forEach((o, i) => {
      const on = i === this.active;
      o.classList.toggle('sift-flydown--active', on);
      o.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const cur = this.options[this.active];
    this.input.setAttribute('aria-activedescendant', cur.id);
    cur.scrollIntoView({ block: 'nearest' });
  }

  onKeydown(e) {
    if (this.failed) return;
    const open = this.root.classList.contains('live-search--active');
    if (e.key === 'ArrowDown') {
      if (!open) return;
      e.preventDefault();
      e.stopPropagation();
      this.move(1);
    } else if (e.key === 'ArrowUp') {
      if (!open) return;
      e.preventDefault();
      e.stopPropagation();
      this.move(-1);
    } else if (e.key === 'Enter') {
      // Only hijack Enter when the user has arrowed onto a suggestion; otherwise
      // let the form submit natively to /search (the no-JS/failure path).
      if (open && this.active >= 0 && this.options[this.active]) {
        e.preventDefault();
        const sel = this.options[this.active];
        sendClickBeacon(this.cfg, sel); // keyboard "click" — same signal
        window.location.href = sel.href;
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    }
  }

  /** Single-shot handoff: unbind Sift, let Empire's native predictive resume. */
  failover() {
    if (this.failed) return;
    this.failed = true;
    this.debounced.cancel();
    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('mousedown', this.onDocDown);
    for (const type of SHIELD_EVENTS) document.removeEventListener(type, this.onShield, true);
    for (const attr of ['role', 'aria-autocomplete', 'aria-expanded', 'aria-controls', 'aria-haspopup', 'aria-activedescendant']) {
      this.input.removeAttribute(attr);
    }
    this.results.textContent = '';
    this.options = [];
    if (this.live) this.live.remove();
    this.close();
    setBackoff();
    // Hand back to native: Empire listens on keyup.live-search (debounced) on the
    // input. With the shield removed, dispatch a bubbling keyup so native fetches
    // the value already typed — no lost keystrokes. A re-dispatched `input` event
    // would be ignored (Empire binds keyup, not input).
    try {
      this.input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
    } catch {
      try {
        this.input.dispatchEvent(new Event('keyup', { bubbles: true }));
      } catch {
        /* no-op */
      }
    }
  }
}

/* ---------------------------- results page ---------------------------- */

class SiftResults {
  constructor(cfg) {
    this.cfg = cfg;
    this.wrapper = document.querySelector('.productgrid--wrapper');
    this.outer = document.querySelector('.productgrid--outer');
    this.sidebar = document.querySelector('.productgrid--sidebar');
    this.onChange = this.onChange.bind(this);
    this.onPop = this.onPop.bind(this);
    this.painted = false;
    // Request sequencing: only the most-recently-initiated search may paint, so
    // out-of-order fetch resolutions (rapid facet toggles, or popstate racing a
    // still-pending mutate) can never desync the grid/sidebar from the URL.
    this.reqSeq = 0;
    this.ctrl = null;
  }

  usable() {
    return !!this.wrapper;
  }

  enhance() {
    // A "see native results" escape hatch — never enhance when asked to stand down.
    if (new URLSearchParams(location.search).get('sift') === 'off') return;
    window.addEventListener('popstate', this.onPop);
    this.renderFromUrl(true);
  }

  onPop() {
    this.renderFromUrl(false);
  }

  async renderFromUrl(replace) {
    const state = readParams(location.search);
    if (!state.q && !MULTI_FACETS.some((f) => state[f].length) && state.available == null && state.on_sale == null) {
      return; // nothing to search — leave native "no search performed" as delivered
    }
    if (replace) {
      try {
        history.replaceState({ sift: true }, '', `${location.pathname}?${buildQuery(state)}`);
      } catch {
        /* ignore */
      }
    }
    const data = await this.search(state);
    if (!data) return; // API failure → native grid remains untouched (first load)
    this.paint(data, state);
  }

  async search(state) {
    // Claim the newest sequence number and abort any in-flight request. A stale
    // fetch that resolves after a newer one was started returns null (below) and
    // never paints, so the displayed grid/facets always match the latest state.
    const myId = ++this.reqSeq;
    if (this.ctrl) this.ctrl.abort();
    const ctrl = (this.ctrl = new AbortController());
    const timer = setTimeout(() => ctrl.abort(), SEARCH_TIMEOUT);
    try {
      // Early-fetch starter (inline in the snippet): consume the in-flight
      // initial-load response, but only if it was started for EXACTLY this
      // location.search and only once. Any miss/failure falls through to a
      // fresh fetch — error behavior is identical to not having a starter.
      let res = null;
      const early = window.__siftEarly;
      if (early && !early.used && early.search === location.search) {
        early.used = true;
        try {
          res = await Promise.race([
            early.promise,
            new Promise((_, reject) =>
              ctrl.signal.addEventListener('abort', () => reject(new Error('abort')), { once: true }),
            ),
          ]);
          if (!res || !res.ok) res = null;
        } catch {
          res = null;
        }
      }
      if (!res) {
        res = await fetch(`${this.cfg.apiBase}/api/search?${buildQuery(state)}`, {
          signal: ctrl.signal,
          headers: { Accept: 'application/json' },
        });
      }
      clearTimeout(timer);
      if (myId !== this.reqSeq) return null; // superseded by a newer search
      if (!res.ok) throw new Error('http');
      return await res.json();
    } catch {
      clearTimeout(timer);
      return null; // failure / abort / supersede → no visible error, no throw
    }
  }

  paint(data, state) {
    this.painted = true;
    const hits = Array.isArray(data.hits) ? data.hits : [];
    // Remove native + prior Sift result regions; Sift owns the grid from here.
    for (const sel of ['.productgrid--items', '.productgrid--no-results', '.sift-results']) {
      this.wrapper.querySelectorAll(sel).forEach((n) => n.remove());
    }
    const region = el('div', { class: 'sift-results' });
    region.appendChild(this.toolbar(data, state));
    if (!hits.length) {
      region.appendChild(this.noResults(state));
      this.paintBestsellers(region, state);
    } else {
      const grid = el('div', { class: 'productgrid--items products-per-row-4 sift-grid' });
      hits.forEach((h, i) => grid.appendChild(this.card(h, i, state)));
      region.appendChild(grid);
    }
    this.wrapper.appendChild(region);
    this.buildSidebar(data, state);
  }

  toolbar(data, state) {
    const bar = el('div', { class: 'sift-toolbar' });
    if (data.corrected) {
      bar.appendChild(
        el('p', {
          class: 'sift-toolbar__corrected',
          text: `Showing results for “${data.corrected}” — no matches for “${state.q}”`,
        }),
      );
    }
    const count = typeof data.nbHits === 'number' ? data.nbHits : (data.hits || []).length;
    bar.appendChild(
      el('p', {
        class: 'sift-toolbar__count',
        text: (this.cfg.strings.resultsCount || '{{count}} results').replace('{{count}}', count),
        attrs: { 'aria-live': 'polite', role: 'status' },
      }),
    );
    // sort
    const label = el('label', { class: 'sift-toolbar__sort' });
    label.appendChild(el('span', { class: 'sift-visually-hidden', text: this.cfg.strings.sortBy || 'Sort by' }));
    const sel = el('select', { class: 'sift-toolbar__sort-select', attrs: { 'data-sift-sort': '' } });
    const opts = [
      ['relevance', this.cfg.strings.sortRelevance || 'Relevance'],
      ['price_asc', this.cfg.strings.sortPriceAsc || 'Price: low to high'],
      ['price_desc', this.cfg.strings.sortPriceDesc || 'Price: high to low'],
      ['newest', this.cfg.strings.sortNewest || 'Newest'],
      ['bestselling', this.cfg.strings.sortBest || 'Best selling'],
    ];
    for (const [v, t] of opts) {
      const o = el('option', { text: t, attrs: { value: v } });
      if (state.sort === v) o.setAttribute('selected', 'selected');
      sel.appendChild(o);
    }
    sel.value = state.sort;
    sel.addEventListener('change', () => this.mutate({ sort: sel.value, page: 1 }));
    label.appendChild(sel);
    bar.appendChild(label);
    return bar;
  }

  card(h, i, state) {
    const item = el('article', { class: 'productgrid--item sift-card' });
    const link = el('a', {
      class: 'productgrid--item-link sift-card__link',
      attrs: { href: abs(h.url, this.cfg.origin) },
    });
    link.dataset.siftPid = String(h.id || '');
    link.dataset.siftRank = String(i || 0);
    link.dataset.siftSurface = 'search';
    link.dataset.siftQ = (state && state.q) || '';
    if (h.image && isCdnImage(h.image.url)) {
      const media = el('div', { class: 'sift-card__media' });
      media.appendChild(
        el('img', {
          class: 'sift-card__img',
          attrs: { src: h.image.url, alt: h.image.alt || h.title || '', loading: 'lazy', width: '360', height: '360' },
        }),
      );
      link.appendChild(media);
    }
    const title = el('h2', { class: 'productitem--title sift-card__title' });
    appendHighlighted(title, h._formatted && h._formatted.title, h.title);
    link.appendChild(title);
    const price = el('div', { class: 'productitem--price sift-card__price' });
    if (h.on_sale) price.classList.add('sift-card__price--sale');
    price.appendChild(el('span', { text: formatMoney(h.price_min, this.cfg.currencySymbol) }));
    if (h.price_max != null && h.price_max > h.price_min) {
      price.appendChild(el('span', { class: 'sift-card__price-plus', text: '+' }));
    }
    link.appendChild(price);
    if (!h.available) {
      link.appendChild(el('span', { class: 'sift-card__badge', text: this.cfg.strings.soldOut || 'Sold out' }));
    }
    item.appendChild(link);
    return item;
  }

  noResults(state) {
    const box = el('div', { class: 'productgrid--no-results sift-no-results' });
    box.appendChild(
      el('h2', {
        class: 'productgrid--no-results-title',
        text: (this.cfg.strings.noResults || 'No matches for "{{q}}"').replace('{{q}}', state.q),
      }),
    );
    // NG4 — link to native search (covers articles/pages) with Sift standing down.
    const native = el('a', {
      class: 'productgrid--no-results-button sift-native-link',
      text: this.cfg.strings.seeNative || 'See all results',
      attrs: { href: `${this.cfg.searchUrl}?q=${encodeURIComponent(state.q)}&sift=off` },
    });
    box.appendChild(native);
    return box;
  }

  async paintBestsellers(region, state) {
    const data = await this.search({ q: '', sort: 'bestselling', page: 1 });
    if (!data || !Array.isArray(data.hits) || !data.hits.length) return;
    region.appendChild(
      el('h3', { class: 'sift-bestsellers__title', text: this.cfg.strings.popular || 'Popular right now' }),
    );
    const grid = el('div', { class: 'productgrid--items products-per-row-4 sift-grid' });
    data.hits.slice(0, 8).forEach((h, i) => grid.appendChild(this.card(h, i, state)));
    region.appendChild(grid);
  }

  buildSidebar(data, state) {
    if (!this.sidebar) return;
    const dist = data.facetDistribution || {};
    this.sidebar.textContent = '';
    const form = el('form', { class: 'sift-facets', attrs: { 'data-sift-facets': '' } });
    form.addEventListener('submit', (e) => e.preventDefault());
    const groups = [
      ['vendor', 'vendor', this.cfg.strings.fVendor || 'Brand'],
      ['product_type', 'product_type', this.cfg.strings.fType || 'Product type'],
      ['category', 'category_lvl0', this.cfg.strings.fCategory || 'Category'],
    ];
    for (const [param, distKey, legend] of groups) {
      const values = dist[distKey];
      if (values && Object.keys(values).length) {
        form.appendChild(this.facetGroup(param, legend, values, state[param]));
      }
    }
    // availability + on sale (boolean facets)
    form.appendChild(this.boolFacet('available', this.cfg.strings.fAvail || 'In stock only', state.available));
    if (dist.on_sale && dist.on_sale.true) {
      form.appendChild(this.boolFacet('on_sale', this.cfg.strings.fSale || 'On sale', state.on_sale));
    }
    form.appendChild(this.priceGroup(state));
    this.sidebar.appendChild(form);
  }

  facetGroup(param, legend, values, selected) {
    const sel = new Set(selected || []);
    const fs = el('fieldset', { class: 'sift-facets__group productgrid--sidebar-section' });
    fs.appendChild(el('legend', { class: 'sift-facets__legend productgrid--sidebar-title', text: legend }));
    const entries = Object.entries(values).sort((a, b) => b[1] - a[1]);
    for (const [value, count] of entries) {
      const lbl = el('label', { class: 'sift-facets__option' });
      const cb = el('input', { attrs: { type: 'checkbox', name: param, value } });
      if (sel.has(value)) cb.setAttribute('checked', 'checked');
      cb.addEventListener('change', this.onChange);
      lbl.appendChild(cb);
      lbl.appendChild(el('span', { class: 'sift-facets__label', text: value }));
      lbl.appendChild(el('span', { class: 'sift-facets__count', text: ` (${count})` }));
      fs.appendChild(lbl);
    }
    return fs;
  }

  boolFacet(param, legend, current) {
    const fs = el('fieldset', { class: 'sift-facets__group productgrid--sidebar-section' });
    const lbl = el('label', { class: 'sift-facets__option' });
    const cb = el('input', { attrs: { type: 'checkbox', name: param, value: 'true' } });
    if (current === true) cb.setAttribute('checked', 'checked');
    cb.addEventListener('change', this.onChange);
    lbl.appendChild(cb);
    lbl.appendChild(el('span', { class: 'sift-facets__label', text: legend }));
    fs.appendChild(lbl);
    return fs;
  }

  priceGroup(state) {
    const fs = el('fieldset', { class: 'sift-facets__group sift-facets__price productgrid--sidebar-section' });
    fs.appendChild(el('legend', { class: 'sift-facets__legend productgrid--sidebar-title', text: this.cfg.strings.fPrice || 'Price' }));
    const mk = (name, ph, val) => {
      const i = el('input', {
        class: 'sift-facets__price-input',
        attrs: { type: 'number', inputmode: 'decimal', min: '0', step: '1', name, placeholder: ph, 'aria-label': ph },
      });
      if (val != null) i.value = String(val);
      i.addEventListener('change', this.onChange);
      return i;
    };
    fs.appendChild(mk('price_min', this.cfg.strings.priceMin || 'Min', state.price_min));
    fs.appendChild(el('span', { class: 'sift-facets__price-sep', text: '–' }));
    fs.appendChild(mk('price_max', this.cfg.strings.priceMax || 'Max', state.price_max));
    return fs;
  }

  /** Gather current control values → new state → push URL → repaint. */
  onChange() {
    const form = this.sidebar.querySelector('[data-sift-facets]');
    const base = readParams(location.search);
    const next = { q: base.q, sort: base.sort, page: 1 };
    for (const f of MULTI_FACETS) {
      next[f] = Array.from(form.querySelectorAll(`input[name="${f}"]:checked`)).map((c) => c.value);
    }
    for (const f of BOOL_FACETS) {
      const c = form.querySelector(`input[name="${f}"]`);
      next[f] = c && c.checked ? true : null;
    }
    const pmin = form.querySelector('input[name="price_min"]');
    const pmax = form.querySelector('input[name="price_max"]');
    next.price_min = pmin && pmin.value !== '' ? Number(pmin.value) : null;
    next.price_max = pmax && pmax.value !== '' ? Number(pmax.value) : null;
    this.mutate(next, true);
  }

  /** Merge a partial change, push history, refetch, repaint. */
  async mutate(partial, full) {
    const state = full ? partial : { ...readParams(location.search), ...partial };
    try {
      history.pushState({ sift: true }, '', `${location.pathname}?${buildQuery(state)}`);
    } catch {
      /* ignore */
    }
    const data = await this.search(state);
    if (!data) return; // keep the current Sift view on a transient refetch failure
    this.paint(data, state);
  }
}

/* ---------------------------- bootstrap ---------------------------- */

export function init(cfg) {
  if (!cfg || !cfg.apiBase) return { flydown: null, results: null };
  installClickBeacon(cfg);
  const out = { flydown: null, results: null };
  if (!backoffActive()) {
    const root = document.querySelector('[data-live-search]');
    if (root) {
      const fly = new SiftFlydown(root, cfg);
      if (fly.usable()) {
        fly.enhance();
        out.flydown = fly;
      }
    }
  }
  if (cfg.isSearch) {
    const res = new SiftResults(cfg);
    if (res.usable()) {
      res.enhance();
      out.results = res;
    }
  }
  return out;
}

// Self-init only in a browser that actually carries the Empire markup + config.
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const boot = () => {
    if (window.SiftConfig) window.__sift = init(window.SiftConfig);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
